import type * as React from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { TopBar } from '@xing-com/crate-layout-top-bar';
import { GridContainer, GridItem } from '@xing-com/grid';
import { Section } from '@xing-com/layout-primitives';
import { mediaSmallOrTiny, mediaWide } from '@xing-com/layout-tokens';
import { SuperEllipse } from '@xing-com/super-ellipse';
import {
  scale070,
  scale080,
  scale150,
  scale160,
  scale190,
  scale220,
  xdlColorText,
} from '@xing-com/tokens';
import { Hero, BodyCopy } from '@xing-com/typography';

import Butterfly from '../assets/logos/svg/xingButterfly.svg';
import MarketingThumb from '../assets/marketing.webp';
import PremiumSeatsThumb from '../assets/premium-seats.webp';
import RecruitingThumb from '../assets/recruiting.webp';
import { BusinessHead } from '../components/business-head/business-head';
import { Card } from '../components/card/card';

const textStyles = css`
  margin: 0;
  padding: ${scale190} 0 ${scale080} 0;
  @media ${mediaSmallOrTiny} {
    padding: ${scale150} 0 ${scale080} 0;
  }
`;
const StyledHero = styled(Hero)`
  ${textStyles}
`;
const StyledBodyCopy = styled(BodyCopy)`
  ${textStyles}
`;

const StyledGridItem = styled(GridItem)`
  margin-bottom: ${scale080};

  @media ${mediaWide} {
    margin-bottom: calc(${scale080} + ${scale220});
  }
`;

const StyledSpanLinkLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${scale070};
  > div {
    min-width: ${scale160};
    min-height: ${scale160};
  }
`;

const StyledSpanLinkLabelText = styled.span`
  padding-left: ${scale070};
  color: ${xdlColorText};
  font-weight: 700;
`;

export const PageHome: React.FC = () => {
  const { track } = useTracking();

  const trackingHandler = (
    event: 'opened' | 'visited',
    element: string
  ): void =>
    track({
      type: 'nwt',
      event,
      eventSchema: 'basic',
      page: 'business_solutions/index',
      sentBy: 'business_solutions',
      element,
    });

  const { formatMessage, locale } = useIntl();

  return (
    <Section noBlockPadding>
      <GridContainer variant="regular">
        <GridItem span={12}>
          <TopBar
            type="subheader"
            title={formatMessage({ id: 'BS_HOME_HEADLINE' })}
            backButtonUrl="/"
            hideLoggedOut
          />
          <BusinessHead
            description={formatMessage({
              id: 'BS_HOME_META_DESCRIPTION',
            })}
            title={formatMessage({
              id: 'BS_HOME_META_TITLE',
            })}
          />
          <StyledHero size="medium" sizeConfined="large" sizeWide="xlarge">
            {formatMessage({ id: 'BS_HOME_HEADLINE' })}
          </StyledHero>
          <StyledBodyCopy size="large" sizeConfined="xlarge">
            {formatMessage({ id: 'BS_HOME_TEXT' })}
          </StyledBodyCopy>
        </GridItem>
        <StyledGridItem span={12} spanMedium={4}>
          <Card
            linkLabel={
              <StyledSpanLinkLabel>
                <SuperEllipse size="small" src={Butterfly} alt="XING logo" />
                <StyledSpanLinkLabelText>
                  {formatMessage({ id: 'BS_ONLYFY_HOME_TALENTS_CTA' })}
                </StyledSpanLinkLabelText>
              </StyledSpanLinkLabel>
            }
            onClick={() => {
              trackingHandler('opened', 'recruiting_solutions');
            }}
            image={RecruitingThumb}
            to={`https://recruiting.xing.com/${locale === 'de' ? 'de' : 'en'}/`}
            headline={formatMessage({
              id: 'BS_ONLYFY_TALENTS_HEADLINE',
            })}
            text={formatMessage({ id: 'BS_ONLYFY_HOME_TALENTS_TEASER' })}
          />
        </StyledGridItem>
        <StyledGridItem span={12} spanMedium={4}>
          <Card
            linkLabel={
              <StyledSpanLinkLabel>
                <SuperEllipse size="small" src={Butterfly} alt="XING logo" />
                <StyledSpanLinkLabelText>
                  {formatMessage({ id: 'BS_HOME_MARKETING_CTA' })}
                </StyledSpanLinkLabelText>
              </StyledSpanLinkLabel>
            }
            image={MarketingThumb}
            onClick={() => {
              trackingHandler('opened', 'marketing_solutions');
            }}
            to="https://werben.xing.com/"
            headline={formatMessage({ id: 'BS_MARKETING_HEADLINE' })}
            text={formatMessage({ id: 'BS_HOME_MARKETING_TEASER' })}
          />
        </StyledGridItem>
        <StyledGridItem span={12} spanMedium={4}>
          <Card
            linkLabel={
              <StyledSpanLinkLabel>
                <SuperEllipse size="small" src={Butterfly} alt="XING logo" />
                <StyledSpanLinkLabelText>
                  {formatMessage({ id: 'BS_PREMIUM_SEATS_CTA' })}
                </StyledSpanLinkLabelText>
              </StyledSpanLinkLabel>
            }
            onClick={() => {
              trackingHandler('visited', 'premium_b2b');
            }}
            image={PremiumSeatsThumb}
            to="https://xing-b2b.convertri.com/premium"
            headline={formatMessage({ id: 'BS_PREMIUM_SEATS_HEADLINE' })}
            text={formatMessage({ id: 'BS_PREMIUM_SEATS_TEASER' })}
          />
        </StyledGridItem>
      </GridContainer>
    </Section>
  );
};
